/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import SelectorComponent from "app/common/SelectorComponent";
import ComparatorLayout from "app/common/ComparatorLayout";
import { useEffectAsync } from "lib/react/customHooks";
import { useSelector, useDispatch } from "react-redux";
import { getEstimations } from "lib/requests/estimations";
import getElasticities from "lib/requests/elasticities";
import getHistorics from "lib/requests/historics";
import { resampleHistoricsToMonthly } from "lib/utils/historics";
import CardComponent from "app/common/CardComponent";
import _ from "lodash";
import moment from "moment";
import NoResultsComponent from "app/common/NoResultsComponent";
import PriceSlider from "./PriceSlider";
import TimeSeriesComponent from "./time_series/TimeSeriesComponent";
import ExplicabilityComponent from "../../explicability-info/ExplicabilityComponent";
import { setTupleData } from "../duck/slices";

const useStyles = makeStyles((theme) => ({
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
  outterContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  sideItemContainer: {
    flex: "1 0 0",
    minWidth: 0,
  },
  vsText: {
    fontWeight: "bold",
    margin: "4px 15px",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      margin: 15,
    },
  },
}));

const ProductSelector = ({
  color,
  products,
  productCatalog,
  onProductChange,
  title,
}) => (
  <SelectorComponent
    style={{
      border: `2px solid ${color}`,
      marginBottom: "15px",
    }}
    defaultOption={0}
    onChange={onProductChange}
    options={_.orderBy(
      _.map(products, (product) => {
        return {
          key: product.product_id,
          label: productCatalog[product.product_id]?.label,
        };
      }),
      "label",
      "asc"
    )}
    title={title}
  />
);

const ProductTweaker = ({
  color,
  products,
  productCatalog,
  pricesData,
  onProductChange,
  onPriceSelected,
  selectorTitle,
  sliderTitle,
  hidePriceSlider = false,
}) => {
  const [product, setProduct] = useState();

  const handleProductChange = (p) => {
    if (p?.key !== product?.key) {
      setProduct(p);
      onProductChange(p);
    }
  };

  return (
    <>
      <ProductSelector
        color={color}
        products={products}
        productCatalog={productCatalog}
        onProductChange={handleProductChange}
        title={selectorTitle}
      />
      {pricesData && !hidePriceSlider && (
        <PriceSlider
          color={color}
          onPriceSelected={onPriceSelected}
          title={sliderTitle}
          data={{
            values: pricesData?.estimatedPrices,
            refValue: pricesData?.referencePrice,
          }}
        />
      )}
    </>
  );
};

export default function ProductComparatorComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [
    catalogSlice,
    filtersSlice,
    competitorsSlice,
  ] = useSelector((state) => [state.catalog, state.filters, state.competitors]);
  const [state, setState] = useState({});
  const [estimations, setEstimations] = useState({});
  const NUM_HISTORIC_MONTHS = 6;
  const MAX_ESTIMATION_MONTHS = 12;
  const [numEstimationMonths, setNumEstimationMonths] = useState(
    MAX_ESTIMATION_MONTHS
  );
  const [lastEstimationDate, setLastEstimationDate] = useState();
  const [dataloadId, setDataloadId] = useState();
  const sidebarFilters = _.pick(filtersSlice.queryFilters, [
    "retailer_id",
    "area_id",
  ]);

  useEffectAsync(
    // Get the last dataload ID from all estimations
    async () => {
      const result = await getEstimations({
        data_source_id: 2,
        group_by: "delivery_id",
        order_by: "delivery_id",
        order_direction: "desc",
        limit: 1,
      });
      return result.data[0].delivery_id;
    },
    setDataloadId,
    []
  );

  const getProducts = async () => {
    const result = await getEstimations({
      ...sidebarFilters,
      data_source_id: 2,
      order_by: ["date"],
      order_direction: "desc",
      delivery_id: dataloadId,
      limit: 1,
      is_product_client: 1,
    });

    const lastDate = result.data[0]?.date;
    setLastEstimationDate(lastDate);

    const queryFilters = {
      ...sidebarFilters,
      data_source_id: 2,
      date: moment(lastDate).format("YYYY-MM-DD"),
      group_by: "product_id",
      delivery_id: dataloadId,
    };

    return Promise.all([
      getEstimations({
        ...queryFilters,
        is_product_client: 1, // Get own products
      }),
      getEstimations({
        ...queryFilters,
        is_product_client: 0, // Get competitors products
      }),
    ]);
  };

  const retrieveProductComparatorForTuple = async (tupleKey) => {
    if (competitorsSlice?.tuples[tupleKey]?.productComparator) {
      return {
        key: tupleKey,
        productComparator: competitorsSlice.tuples[tupleKey].productComparator,
      };
    }

    const [
      ownProductsResponse,
      competitorsProductsResponse,
    ] = await getProducts();

    return {
      key: tupleKey,
      productComparator: {
        ownProducts: ownProductsResponse.data,
        competitorsProducts: competitorsProductsResponse.data,
      },
    };
  };

  const updateProductData = async (product, isOwn = false) => {
    const key = isOwn ? "own" : "competitors";

    if (state[key]?.selectedProduct?.key === product.key) return;

    const tupleData = {
      ...competitorsSlice.tuples[state.tupleKey],
      ownSelectedProduct: isOwn && product,
      competitorSelectedProduct: !isOwn && product,
    };
    dispatch(
      setTupleData({
        [state.tupleKey]: tupleData,
      })
    );

    const queryFilters = {
      ...sidebarFilters,
      product_id: product.key,
      data_source_id: 2,
    };

    const result = await getHistorics({
      ...queryFilters,
      order_by: "date",
      order_direction: "desc",
      limit: 1,
    });

    const lastHistoricDate = result.data[0]?.date;
    setNumEstimationMonths(
      Math.min(
        MAX_ESTIMATION_MONTHS,
        moment(lastEstimationDate).diff(moment(lastHistoricDate), "months")
      )
    );

    const getPrices = async (filters) => {
      const res = await getEstimations({
        ...filters,
        delivery_id: dataloadId,
        group_by: ["price"],
        min_date: moment(lastHistoricDate)
          .add(1, "month")
          .date(15)
          .format("YYYY-MM-DD"),
        max_date: moment(lastHistoricDate)
          .add(numEstimationMonths, "months")
          .date(15)
          .format("YYYY-MM-DD"),
      });

      return _.map(res.data, (estimation) => estimation.price);
    };

    const [historicsResult, prices] = await Promise.all([
      getHistorics({
        ...queryFilters,
        group_by: "date",
        sum: ["units"],
        avg_weight_by_units: ["price"],
        min_date: moment(lastHistoricDate)
          .subtract(NUM_HISTORIC_MONTHS, "months")
          .format("YYYY-MM-DD"),
        max_date: moment(lastHistoricDate).format("YYYY-MM-DD"),
        order_by: "date",
        order_direction: "asc",
      }),
      getPrices(queryFilters),
    ]);

    // historical_data can come back at daily/weekly cadence instead of one
    // row per month; resample so the time series chart keeps seeing ~1 row
    // per month, as before.
    const monthlyHistorics = _.orderBy(
      resampleHistoricsToMonthly(historicsResult.data),
      "date",
      "asc"
    );

    setState((prevState) => {
      const newState = {
        ...prevState,
      };
      newState[key] = {
        ...prevState[key],
        lastHistoricDate,
        selectedProduct: product,
        [product.key]: {
          historics: monthlyHistorics,
          referencePrice: monthlyHistorics.slice(-1).pop()
            ?.avg_weight_by_units_price,
          estimatedPrices: prices,
        },
      };
      return newState;
    });
  };

  useEffectAsync(
    async () => {
      const tupleKey = `productComparator-${filtersSlice.filtersKey}`;

      setState({
        ...state,
        isDataReady: false,
      });

      return dataloadId && retrieveProductComparatorForTuple(tupleKey);
    },
    (result) => {
      if (!dataloadId) return;

      const { ownProducts, competitorsProducts } = result.productComparator;

      dispatch(
        setTupleData({
          [result.key]: result.productComparator,
        })
      );

      setState({
        ...state,
        isDataReady: true,
        tupleKey: result.key,
        own: {
          ...state.own,
          products: ownProducts,
        },
        competitors: {
          ...state.competitors,
          products: competitorsProducts,
        },
      });

      // If the previously selected product (if any) is found in the array of products,
      // then it means that the product selection was not changed due to filters change and
      // it must trigger the product data update
      if (
        _.find(
          ownProducts,
          (product) => product.product_id === state?.own?.selectedProduct?.key
        )
      )
        updateProductData(state?.own?.selectedProduct, true);
      if (
        _.find(
          ownProducts,
          (product) =>
            product.product_id === state?.competitors?.selectedProduct?.key
        )
      )
        updateProductData(state?.competitors?.selectedProduct);
    },
    [filtersSlice.filters.retailer, filtersSlice.filters.area, dataloadId]
  );

  const getCrossAdjustments = async () => {
    const lastOwnHistoricDate = moment(state?.own?.lastHistoricDate);
    const lastCompetitorHistoricDate = moment(
      state?.competitors?.lastHistoricDate
    );
    const queryFilters = {
      ...sidebarFilters,
      product_id: [
        state?.own?.selectedProduct?.key,
        state?.competitors?.selectedProduct?.key,
      ],
      source_product_id: [
        state?.own?.selectedProduct?.key,
        state?.competitors?.selectedProduct?.key,
      ],
      min_date: moment
        .min(lastOwnHistoricDate, lastCompetitorHistoricDate)
        .add(1, "month")
        .date(15)
        .format("YYYY-MM-DD"),
      max_date: moment
        .max(lastOwnHistoricDate, lastCompetitorHistoricDate)
        .add(numEstimationMonths, "months")
        .date(15)
        .format("YYYY-MM-DD"),
      group_by: [
        "source_product_id",
        "product_id",
        "date",
        "retailer_id",
        "area_id",
        "constant",
      ],
      data_source_id: 2,
    };

    if (dataloadId) queryFilters.delivery_id = dataloadId;

    const el = await getElasticities(queryFilters);
    return el;
  };

  useEffectAsync(
    async () => {
      if (state?.own?.selectedProduct && state?.competitors?.selectedProduct) {
        // Query elasticities
        const response = getCrossAdjustments();
        return response;
      }
      return null;
    },
    (result) => {
      if (result) {
        setState((prevState) => ({
          ...prevState,
          crossAdjustments: _.keyBy(
            result.data,
            (adjustment) =>
              `${adjustment.date}-${adjustment.source_product_id}-${adjustment.product_id}-${adjustment.area_id}-${adjustment.retailer_id}`
          ),
        }));
      }
    },
    [state?.own?.selectedProduct, state?.competitors?.selectedProduct]
  );

  const getEstimationsByProduct = async (productId, isOwn = false) => {
    if (estimations[productId]) return estimations[productId];

    const key = isOwn ? "own" : "competitors";
    const queryFilters = {
      ...sidebarFilters,
      product_id: productId,
      min_date: moment(state[key]?.lastHistoricDate)
        .add(1, "month")
        .format("YYYY-MM-DD"),
      max_date: moment(state.own?.lastHistoricDate)
        .add(numEstimationMonths, "months")
        .format("YYYY-MM-DD"),
      data_source_id: 2,
      delivery_id: dataloadId,
      group_by: [
        "date",
        "product_id",
        "area_id",
        "retailer_id",
        "pct_price_cross",
        "units",
        "price",
      ],
    };
    // Query estimations filtered by price for the next N months
    const response = await getEstimations(queryFilters);

    setEstimations({
      ...estimations,
      [productId]: response.data,
    });

    return response.data;
  };

  const handleSelectedPrice = async (price, isOwn = false) => {
    /**
     * Competitors data is dependent on state.own.lastHistoricPrice, so the competitor's
     * price slider component must wait until state.own.lastHistoricPrice variable is ready
     */
    const key = isOwn ? "own" : "competitors";

    const productEstimations = await getEstimationsByProduct(
      state[key]?.selectedProduct.key,
      isOwn
    );

    const estimationsBySelectedPrice = _.filter(
      productEstimations,
      (estimation) => estimation.price === price
    );

    setState((prevState) => {
      const newState = {
        ...prevState,
      };
      newState[key] = {
        ...prevState[key],
        selectedPrice: price,
        estimations: _.keyBy(
          estimationsBySelectedPrice,
          (estimation) =>
            `${estimation.date}-${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
        ),
      };
      return newState;
    });
  };

  // const OwnProductsTweaker = () => (
  //   <ProductTweaker
  //     color="#10A2B1"
  //     products={state.own?.products}
  //     productCatalog={catalogSlice.products}
  //     pricesData={state?.own?.[state?.own?.selectedProduct?.key]}
  //     onProductChange={(product) => updateProductData(product, true)}
  //     onPriceSelected={(price) => handleSelectedPrice(price, true)}
  //   />
  // );

  // const CompetitorsProductsTweaker = () => (
  //   <ProductTweaker
  //     color="#FFC84A"
  //     products={state?.competitors?.products}
  //     productCatalog={catalogSlice.competitors}
  //     pricesData={
  //       state?.competitors?.[state?.competitors?.selectedProduct?.key]
  //     }
  //     onProductChange={updateProductData}
  //     onPriceSelected={handleSelectedPrice}
  //     hidePriceSlider={!state?.own?.lastHistoricDate}
  //   />
  // );

  // const ProductComparator = () => {
  //   // return (
  //   //   <ComparatorLayout
  //   //     left={<OwnProductsTweaker />}
  //   //     center={() => (
  //   //       <Typography variant="body1" className={classes.vsText}>
  //   //         vs
  //   //       </Typography>
  //   //     )}
  //   //     right={<CompetitorsProductsTweaker />}
  //   //   />
  //   // );
  //   return (
  //     <Box className={classes.outterContainer}>
  //       <Box className={classes.sideItemContainer}>
  //         <OwnProductsTweaker />
  //       </Box>
  //       <Typography variant="body1" className={classes.vsText}>
  //         vs
  //       </Typography>
  //       <Box className={classes.sideItemContainer}>
  //         <CompetitorsProductsTweaker />
  //       </Box>
  //     </Box>
  //   );
  // };

  return (
    <CardComponent
      title={
        <Typography variant="body1" className={classes.componentTitle}>
          Product comparison
          <ExplicabilityComponent tooltipText="Select the two products to be compared, set their prices and see their corresponding volume according to the AI model + cross effects compared to the past" />
        </Typography>
      }
      body={
        <>
          {state.isDataReady && (
            <>
              {state.own?.products?.length ? (
                <>
                  <ComparatorLayout
                    left={
                      <ProductTweaker
                        color="#10A2B1"
                        products={state.own?.products}
                        productCatalog={catalogSlice.products}
                        pricesData={
                          state?.own?.[state?.own?.selectedProduct?.key]
                        }
                        onProductChange={(product) =>
                          updateProductData(product, true)
                        }
                        onPriceSelected={(price) =>
                          handleSelectedPrice(price, true)
                        }
                        selectorTitle="My Products"
                        sliderTitle="My Price"
                      />
                    }
                    center={() => (
                      <Typography variant="body1" className={classes.vsText}>
                        vs
                      </Typography>
                    )}
                    right={
                      <ProductTweaker
                        color="#FFC84A"
                        products={state?.competitors?.products}
                        productCatalog={catalogSlice.competitors}
                        pricesData={
                          state?.competitors?.[
                            state?.competitors?.selectedProduct?.key
                          ]
                        }
                        onProductChange={updateProductData}
                        onPriceSelected={handleSelectedPrice}
                        selectorTitle="Competitor's Products"
                        sliderTitle="Competitor's Price"
                        hidePriceSlider={!state?.own?.lastHistoricDate}
                      />
                    }
                  />

                  <Box marginTop="10px" flexGrow="1" minHeight="0">
                    {state?.own?.selectedProduct &&
                      state?.competitors?.selectedProduct && (
                        <TimeSeriesComponent
                          data={state}
                          numHistoricMonths={NUM_HISTORIC_MONTHS}
                          numEstimationMonths={numEstimationMonths}
                        />
                      )}
                  </Box>
                </>
              ) : (
                <NoResultsComponent />
              )}
            </>
          )}
        </>
      }
    />
  );
}
